/**
 * Change 19 §4's audience, as data rather than JSX.
 *
 * 🔴 THE TWO LISTS IN A BROADCAST MEAN OPPOSITE THINGS BY THEIR ABSENCE, and
 * that is the single fact this file exists to keep straight:
 *
 *   - no role chosen   → the broadcast is **refused**. It would reach nobody,
 *     and a write that reaches nobody looks exactly like one that worked.
 *   - no school chosen → the broadcast goes to **every school**. This is the
 *     ordinary case — "all parents" — and it is what §4 means by contrast with
 *     *"or specific groups"*.
 *
 * Getting them the same way round is the bug that sends a term-start notice to
 * nobody, or a single school's maintenance warning to the whole platform.
 */

/**
 * The roles an operator may write to, in the order they are offered.
 *
 * 🔴 PARENTS FIRST, BECAUSE §4 NAMES THEM FIRST AND THEY ARE THE LARGEST AND
 * MOST CONSEQUENTIAL AUDIENCE. §4 names three — *"Parents, Teachers, School
 * Admins"* — and `PRINCIPAL` is here because a head is an adult who reads a
 * school's post and is not a school admin.
 *
 * ⚠️ `DEVICE` IS ABSENT AND MUST STAY ABSENT — it is not in the server's
 * `BROADCAST_ROLES` either. A device token is a four-year-old's laptop: there is
 * no adult behind it and no chrome to draw an adult's message in.
 */
export const BROADCAST_AUDIENCES = [
  { role: "PARENT", label: "Parents", hint: "Every linked parent at the schools chosen below." },
  { role: "TEACHER", label: "Teachers", hint: "Class teachers." },
  { role: "ADMIN", label: "School admins", hint: "The people who run a school on the platform." },
  { role: "PRINCIPAL", label: "Principals", hint: "Heads, who read but do not post." },
] as const;

/**
 * Roles the picker no longer offers, kept for the chips on broadcasts ALREADY
 * SENT.
 *
 * 🔴 DELETING A ROW FROM THE LIST ABOVE IS NOT ENOUGH ON ITS OWN. `roleLabel`
 * falls back to the raw value, so a broadcast sent last term to coordinators
 * would start rendering its audience as "COORDINATOR" — the enum leaking onto a
 * screen, on a row nobody can edit to fix it. The server's `BROADCAST_ROLES`
 * still accepts both; what changed is only what this form offers.
 */
export const RETIRED_AUDIENCE_LABELS: Readonly<Record<string, string>> = {
  COORDINATOR: "Coordinators",
  SUPER_ADMIN: "Platform operators",
};

export type BroadcastRole = (typeof BROADCAST_AUDIENCES)[number]["role"];

/** How many schools the server will accept by name in one broadcast. */
export const BROADCAST_SCHOOLS_MAX = 200;

export const TITLE_MAX = 120;
export const BODY_MAX = 600;

export interface BroadcastDraft {
  title: string;
  body: string;
  roles: readonly BroadcastRole[];
  /** 🔴 Empty is every school — see the header. */
  schoolIds: readonly string[];
}

export interface BroadcastDraftErrors {
  title: string | null;
  body: string | null;
  roles: string | null;
  schoolIds: string | null;
}

export function validateBroadcastDraft(draft: BroadcastDraft): BroadcastDraftErrors {
  const title = draft.title.trim();
  const body = draft.body.trim();

  return {
    title:
      title === ""
        ? "A broadcast needs a headline — this is the line people read without opening it."
        : title.length > TITLE_MAX
          ? `A headline is at most ${String(TITLE_MAX)} characters.`
          : null,
    body:
      body === ""
        ? "Say what changed. A headline on its own is a notification, not a message."
        : body.length > BODY_MAX
          ? `A broadcast is at most ${String(BODY_MAX)} characters — link to the detail instead.`
          : null,
    /* 🔴 The refusal the server also makes. Stated twice on purpose: the form
       should not be able to send a body that cannot succeed. */
    roles:
      draft.roles.length === 0
        ? "Choose at least one audience. A broadcast addressed to nobody reaches nobody."
        : null,
    schoolIds:
      draft.schoolIds.length > BROADCAST_SCHOOLS_MAX
        ? `Name at most ${String(BROADCAST_SCHOOLS_MAX)} schools, or none at all for every school.`
        : null,
  };
}

export const broadcastDraftIsValid = (draft: BroadcastDraft): boolean =>
  Object.values(validateBroadcastDraft(draft)).every((m) => m === null);

/** Toggle one role in the draft, keeping the offered order. */
export function toggleRole(
  roles: readonly BroadcastRole[],
  role: BroadcastRole,
): BroadcastRole[] {
  const next = new Set(roles);
  if (next.has(role)) next.delete(role);
  else next.add(role);
  return BROADCAST_AUDIENCES.map((a) => a.role).filter((r) => next.has(r));
}

/**
 * Who this broadcast is about to reach, in one sentence.
 *
 * 🔴 IT IS THE LAST PLACE THE SCHOOL SCOPE IS VISIBLE BEFORE THE WRITE, and the
 * one that has to say "every school" out loud. A screen that shows an empty
 * school picker and no sentence is a screen where "I didn't pick any schools"
 * and "this goes to all of them" look identical.
 */
/**
 * What a role reads as — an offered one, a retired one, or, failing both, the
 * value itself so the screen says something rather than nothing.
 */
export const audienceLabel = (role: string): string =>
  BROADCAST_AUDIENCES.find((a) => a.role === role)?.label ??
  RETIRED_AUDIENCE_LABELS[role] ??
  role;

export function describeAudience(
  draft: BroadcastDraft,
  schoolNameOf: (id: string) => string,
): string {
  if (draft.roles.length === 0) return "Nobody — choose an audience.";

  const who = draft.roles.map(audienceLabel).join(", ");

  if (draft.schoolIds.length === 0) return `${who} at every school on the platform.`;
  if (draft.schoolIds.length <= 3) {
    return `${who} at ${draft.schoolIds.map(schoolNameOf).join(", ")}.`;
  }
  return `${who} at ${String(draft.schoolIds.length)} named schools.`;
}
